import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import { IconButton, Tooltip } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import React, { useEffect, useState } from 'react';

export type FullWidthTogglePosition = {
  top?: number | string;
  right?: number | string;
  bottom?: number | string;
  left?: number | string;
};

export type FullWidthToggleMAIQProps = {
  /** One or more sections whose width should toggle */
  targetRefs:
    | React.RefObject<HTMLElement | null>
    | Array<React.RefObject<HTMLElement | null>>;
  /** Constrained width. Default: 1200 */
  constrainedMaxWidth?: number | string;
  /** Expanded width. Default: '100%' */
  expandedMaxWidth?: number | string;
  defaultExpanded?: boolean;
  /** Absolute offsets. Default: { top: 8, right: 8 } */
  position?: FullWidthTogglePosition;
  tooltipExpand?: string;
  tooltipCollapse?: string;
  sx?: SxProps<Theme>;
};

const toCssSize = (value: number | string): string =>
  typeof value === 'number' ? `${value}px` : value;

const normalizeRefs = (
  targetRefs:
    | React.RefObject<HTMLElement | null>
    | Array<React.RefObject<HTMLElement | null>>,
): Array<React.RefObject<HTMLElement | null>> =>
  Array.isArray(targetRefs) ? targetRefs : [targetRefs];

const applyWidthToTargets = (
  refs: Array<React.RefObject<HTMLElement | null>>,
  maxWidth: number | string,
): void => {
  const cssMaxWidth = toCssSize(maxWidth);
  refs.forEach(ref => {
    const el = ref.current;
    if (!el) return;
    el.style.maxWidth = cssMaxWidth;
    el.style.width = '100%';
  });
};

/**
 * Absolute icon control that toggles one or more target sections between a
 * constrained max-width and full width. Render inside a `position: relative` ancestor.
 */
export default function FullWidthToggleMAIQ({
  targetRefs,
  constrainedMaxWidth = 1200,
  expandedMaxWidth = '100%',
  defaultExpanded = false,
  position,
  tooltipExpand = 'Expand to full width',
  tooltipCollapse = 'Collapse to normal width',
  sx,
}: FullWidthToggleMAIQProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  useEffect(() => {
    applyWidthToTargets(
      normalizeRefs(targetRefs),
      isExpanded ? expandedMaxWidth : constrainedMaxWidth,
    );
  }, [isExpanded, targetRefs, expandedMaxWidth, constrainedMaxWidth]);

  const handleToggle = () => {
    setIsExpanded(prev => !prev);
  };

  const {
    top = 8,
    right = 8,
    bottom,
    left,
  } = position ?? {};

  return (
    <Tooltip title={isExpanded ? tooltipCollapse : tooltipExpand}>
      <IconButton
        aria-label={isExpanded ? tooltipCollapse : tooltipExpand}
        onClick={handleToggle}
        size="medium"
        sx={{
          position: 'absolute',
          zIndex: 11,
          top,
          right: left === undefined ? right : undefined,
          bottom,
          left,
          width: 40,
          height: 40,
          color: 'text.secondary',
          bgcolor: 'background.paper',
          border: 1,
          borderColor: 'divider',
          boxShadow: '0 4px 14px 0 rgba(0,0,0,0.18)',
          '&:hover': {
            bgcolor: 'background.paper',
            color: 'text.primary',
            boxShadow: '0 6px 18px 0 rgba(0,0,0,0.22)',
          },
          ...sx,
        }}
      >
        {isExpanded ? (
          <CloseFullscreenIcon sx={{ fontSize: '1.35rem' }} />
        ) : (
          <OpenInFullIcon sx={{ fontSize: '1.35rem' }} />
        )}
      </IconButton>
    </Tooltip>
  );
}
